import React, { useRef } from 'react';
import { cx } from 'emotion';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { Tooltip } from '@ublend-npm/aulaui-next';
import {
  evaluateMaterialAction,
  MATERIAL_PERMISSIONS,
} from '@core/materials/permissions';

import { DND_TYPE } from './MaterialListItem.dnd';
import { AddSectionListItem, AddIcon } from './AddSectionButton.styled';
import { buttonItem } from './SectionListItem.styled';
import { listItem } from './MaterialListItem.styled';

const CLASSES = {
  text: 'addNewPageLabel',
};
const style = {
  listStyle: 'none',
  padding: 0,
};

const AddSectionButton = ({
  onDrop,
  addSection,
  canCreate = true,
  canAccessEducatorArea = false,
  educatorOnly = false,
  permissions = [],
  isRbacEnabled = false,
  onPermissionDenied = () => {},
  spaceId,
}) => {
  const buttonRef = useRef(null);
  // If RBAC is disabled, always use canCreate as fallback (legacy behavior)
  // If RBAC is enabled, only use fallback if no permissions are resolved
  const hasResolvedPermissions =
    Array.isArray(permissions) && permissions.length > 0;
  const fallbackAllowed = !isRbacEnabled
    ? canCreate
    : !hasResolvedPermissions && canCreate;

  // If RBAC is disabled, pass empty permissions to force fallback usage
  const effectivePermissions = !isRbacEnabled ? [] : permissions;

  // If in educator area and user has educator area permission, allow creating pages
  const hasEducatorAreaAccess = educatorOnly && canAccessEducatorArea;

  const createStatus = evaluateMaterialAction('CREATE_PAGE', {
    permissions: effectivePermissions,
    fallback: fallbackAllowed,
  });

  // Override createStatus if user has educator area permission in educator area
  const finalCreateStatus = hasEducatorAreaAccess
    ? { allowed: true }
    : createStatus;
  const [{ isDraggingOver }, drop] = useDrop({
    accept: DND_TYPE,
    canDrop: (item) => finalCreateStatus.allowed && !item.isFolder,
    collect: (monitor) => ({
      isDraggingOver: monitor.canDrop() && monitor.isOver(),
    }),
    drop: (item, monitor) => {
      if (!finalCreateStatus.allowed || monitor.didDrop()) {
        return;
      }

      onDrop(item.materialId);
    },
  });

  drop(buttonRef);

  const handleClick = () => {
    if (!finalCreateStatus.allowed) {
      onPermissionDenied &&
        onPermissionDenied(MATERIAL_PERMISSIONS.CREATE, spaceId);
      return;
    }
    addSection();
  };

  return (
    <Tooltip
      title={finalCreateStatus.allowed ? 'New page' : finalCreateStatus.message}
      position="top"
    >
      <ul ref={buttonRef} style={style}>
        <AddSectionListItem
          attributes={{
            'data-testid': 'add-folder-page',
            'aria-disabled': !finalCreateStatus.allowed,
          }}
          classes={CLASSES}
          className={cx(listItem, buttonItem)}
          textTruncate
          primaryText="New page"
          leftDecoration={<AddIcon />}
          onClick={handleClick}
          dropType={isDraggingOver ? 'before' : null}
        />
      </ul>
    </Tooltip>
  );
};

AddSectionButton.propTypes = {
  onDrop: PropTypes.func.isRequired,
  addSection: PropTypes.func.isRequired,
  canCreate: PropTypes.bool,
  canAccessEducatorArea: PropTypes.bool,
  educatorOnly: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isRbacEnabled: PropTypes.bool,
  onPermissionDenied: PropTypes.func,
  spaceId: PropTypes.string,
};

AddSectionButton.defaultProps = {
  canCreate: true,
  canAccessEducatorArea: false,
  educatorOnly: false,
  permissions: [],
  isRbacEnabled: false,
  onPermissionDenied: () => {},
  spaceId: undefined,
};

export default AddSectionButton;
